import {
  calculateRewardProgress,
  formatRewardMoney,
  highestGoalCents,
  readCartFromEvent,
  readCartTotals,
  readGiftPropertyKey,
  readRewardGoals,
  readRuleThresholds
} from '@theme/cart-rewards-config'
import { Component } from '@theme/component'
import { ThemeEvents } from '@theme/events'

/**
 * @typedef {import('@theme/cart-rewards-config').RewardGoal} RewardGoal
 */

class CartRewardsComponent extends Component {
  /** @type {RewardGoal[]} */
  #goals = []

  #giftPropertyKey = '_reward'

  /** @param {Event} event */
  #handleDialogOpen = (event) => {
    const target = event.target
    if (target instanceof Element && target.closest('cart-drawer')) {
      this.#refreshFromCart()
    }
  }

  /** @param {Event} event */
  #handleCartUpdate = (event) => {
    const cart = readCartFromEvent(event)
    if (cart) {
      this.apply(cart)
      return
    }
    this.#refreshFromCart()
  }

  connectedCallback() {
    super.connectedCallback()
    this.#readConfiguration()
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate)
    document.addEventListener(
      ThemeEvents.discountUpdate,
      this.#handleCartUpdate
    )
    document.addEventListener('someone:atc:updated', this.#handleCartUpdate)
    document.addEventListener('dialog:open', this.#handleDialogOpen)
    this.#refreshFromCart()
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate)
    document.removeEventListener(
      ThemeEvents.discountUpdate,
      this.#handleCartUpdate
    )
    document.removeEventListener('someone:atc:updated', this.#handleCartUpdate)
    document.removeEventListener('dialog:open', this.#handleDialogOpen)
  }

  #readConfiguration() {
    this.#giftPropertyKey = readGiftPropertyKey(document)
    this.#goals = readRewardGoals(
      this.#stepElements(),
      readRuleThresholds(document)
    )
  }

  /** @returns {HTMLElement[]} */
  #stepElements() {
    const steps = this.refs.steps
    if (!steps) {
      return []
    }
    return Array.isArray(steps) ? steps : [steps]
  }

  async #refreshFromCart() {
    try {
      const response = await fetch(`${this.#cartRoot()}cart.js`)
      if (!response.ok) {
        return
      }
      this.apply(await response.json())
    } catch {}
  }

  /** @returns {string} */
  #cartRoot() {
    const root = window.Shopify?.routes?.root || '/'
    return root.endsWith('/') ? root : `${root}/`
  }

  /**
   * @param {object} cart
   * @returns {void}
   */
  apply(cart) {
    if (!cart) {
      return
    }
    if (this.#goals.length === 0) {
      this.#readConfiguration()
    }
    const totals = readCartTotals(cart, this.#giftPropertyKey)
    this.#render(totals.subtotalCents, totals.itemCount)
  }

  /**
   * @param {number} reachedCents
   * @param {number} itemCount
   * @returns {void}
   */
  #render(reachedCents, itemCount) {
    this.#applyVisibility(itemCount)
    const nextGoal = this.#goals.find((goal) => goal.goalCents > reachedCents)
    this.#renderBar(reachedCents)
    this.#renderPrompt(nextGoal, reachedCents)
  }

  /** @param {number} itemCount */
  #applyVisibility(itemCount) {
    if (this.dataset.visibility !== 'only_with_items') {
      return
    }
    if (this.dataset.designMode === 'true') {
      return
    }
    this.toggleAttribute('hidden', itemCount === 0)
  }

  /** @param {number} reachedCents */
  #renderBar(reachedCents) {
    const { ratio } = calculateRewardProgress(
      reachedCents,
      highestGoalCents(this.#goals)
    )
    const { fill, track } = this.refs
    fill?.style.setProperty('--cart-rewards-progress', `${ratio}%`)
    track?.setAttribute('aria-valuenow', String(Math.round(ratio)))
  }

  /**
   * @param {RewardGoal | undefined} nextGoal
   * @param {number} reachedCents
   */
  #renderPrompt(nextGoal, reachedCents) {
    const { prompt } = this.refs
    if (!prompt) {
      return
    }
    if (this.#goals.length === 0) {
      prompt.textContent = ''
      return
    }
    if (!nextGoal) {
      prompt.textContent = this.dataset.finalText || ''
      return
    }
    const { remainingCents } = calculateRewardProgress(
      reachedCents,
      nextGoal.goalCents
    )
    const money = formatRewardMoney(
      remainingCents,
      this.dataset.locale || 'pt-BR',
      this.dataset.currency || 'BRL'
    )
    prompt.textContent = nextGoal.remainingTemplate.replace(
      '{remaining_amount}',
      money
    )
  }
}

if (!customElements.get('cart-rewards')) {
  customElements.define('cart-rewards', CartRewardsComponent)
}
